import Joi from 'joi'
import errorsResolver from '../schemeUtils'
import moment from 'moment'

const LAST_NAME_LENGTH = 100
const FIRST_NAME_LENGTH = 100
const MIDDLE_NAME_LENGTH = 100
const DISCOUNT_CARD__LENGTH = 40
const AIM_LENGTH = 100
const PLACE_OF_BIRTH_LENGTH = 200
const OTHER_INFORMATION_LENGTH = 2000
const DESCRIPTION_LENGTH = 3000
const PAYER_NUMBER_LENGTH = 40
const HEAR_SOURCE_LENGTH = 100
const PERSONAL_NUMBER = 50
const PROHIBITION_LENGTH = 100
const LACK_IDENTIFIER_DATE = 1000

const patterns = {
  GENERAL_TEXT: (length) => new RegExp(
    `^[a-zA-Zа-яА-ЯёЁ0-9() *\\/?!%{}[\\]^<>,-.|№"_=&$#~@+'\\\\ \n\r:;«»\`]{1,${length}}$`)
}

const createOtherInformationSchema = (length) => {
  return Joi.string()
    .allow('')
    .trim()
    .max(length)
    .pattern(patterns.GENERAL_TEXT(length))
    .error(errorsResolver({
      max: `Поле должно содержать до ${length} символов`,
      pattern: `Поле содержит некорректные символы`
    }));
};

export const clientInformationProperties = {
  otherInformation: createOtherInformationSchema(DESCRIPTION_LENGTH),
  lackIdentifierDataReason: createOtherInformationSchema(LACK_IDENTIFIER_DATE),
  description: createOtherInformationSchema(DESCRIPTION_LENGTH)
};

const clientPersonalInfoProperties = {
  lastName: Joi.string()
    .trim()
    .max(LAST_NAME_LENGTH)
    .required()
    .pattern(new RegExp(`^[a-zA-Zа-яА-ЯёЁ\\- ]{1,${LAST_NAME_LENGTH}}$`))
    .error(errorsResolver({
      max: `Фамилия должна быть до ${LAST_NAME_LENGTH} символов`,
      required: `Фамилия обязательное поле`,
      pattern: `Фамилия содержит некорректные символы`
    })),
  firstName: Joi.string()
    .trim()
    .max(FIRST_NAME_LENGTH)
    .required()
    .pattern(new RegExp(`^[a-zA-Zа-яА-ЯёЁ\\- ]{1,${FIRST_NAME_LENGTH}}$`))
    .error(errorsResolver({
      max: `Собственное имя должно быть до ${FIRST_NAME_LENGTH} символов`,
      required: `Собственное имя обязательное поле`,
      pattern: `Собственное имя содержит некорректные символы`
    })),
  middleName: Joi.string()
    .trim()
    .max(MIDDLE_NAME_LENGTH)
    .allow('')
    .pattern(new RegExp(`^[a-zA-Zа-яА-ЯёЁ\\- ]{1,${MIDDLE_NAME_LENGTH}}$`))
    .error(errorsResolver({
      max: `Отчество должно быть до ${MIDDLE_NAME_LENGTH} символов`,
      required: `Отчество обязательное поле`,
      pattern: `Отчество содержит некорректные символы`
    })),
  gender: Joi.string().required().valid('Мужской', 'Женский')
    .error(errorsResolver({
      required: `Пол обязательное поле`,
      values: `Пол обязательное поле`
    })),
  placeOfBirth: Joi.string()
    .trim()
    .max(PLACE_OF_BIRTH_LENGTH)
    .required()
    .pattern(new RegExp(`^[.,a-zA-Zа-яА-ЯёЁ0-9\\-№()/\\\\ ]{1,${PLACE_OF_BIRTH_LENGTH}}$`))
    .error(errorsResolver({
      max: `Место рождения должно быть до ${PLACE_OF_BIRTH_LENGTH} символов`,
      required: `Место рождения обязательное поле`,
      pattern: `Место рождения содержит некорректные символы`
    })),
  dateOfBirth: Joi.date()
    .required()
    .iso()
    .less(moment().subtract(18, 'year').valueOf())
    .greater(moment().subtract(120, 'years').valueOf()) 
    .error(errorsResolver({
      dateFormat: `Некорректная дата рождения`,
      dateLess: `Возраст клиента меньше 18`,
      dateGreater: 'Возраст не может превышать 120 лет'
    })),
  citizenship: Joi.alternatives().conditional(Joi.object(), {
    then: Joi.object({
      label: Joi.string()
        .required('')
        .error(errorsResolver({
          required: `Гражданство обязательное поле`
        })),
      value: Joi.number()
        .error(errorsResolver({
          number: `Должно быть число`
        }))
    })
      .invalid(null)
      .required()
      .error(errorsResolver({
        invalid: `Гражданство обязательное поле`,
        required: `Гражданство обязательное поле`
      })),
    otherwise: Joi.string()
      .required()
      .invalid(null)
      .error(errorsResolver({
        invalid: `Гражданство обязательное поле`,
        required: `Гражданство обязательное поле`
      }))
  }),
  residency: Joi.bool(),
  personalNumber: Joi.string()
    .when('residency',
      {
        is: true,
        then: Joi.string()
          .pattern(new RegExp('^\\d\\d\\d\\d\\d\\d\\d[A-Z]\\d\\d\\d[A-Z]{2}\\d$'))
          .required()
          .error(errorsResolver({
            required: `Идентификационный номер обязательное поле`,
            pattern: `Идентификационный номер должен быть в формате nnnnnnnAnnnAAn`,
          })),
        otherwise: Joi.string()
          .allow('')
          .max(PERSONAL_NUMBER)
          .pattern(new RegExp(`^[a-zA-Z0-9]{1,${PERSONAL_NUMBER}}$`))
          .error(errorsResolver({
            max: `Идентификационный номер должен содержать до ${PERSONAL_NUMBER} символов`,
            pattern: 'Идентификационный номер содержит некорректные символы',
          })),
      }),
  discountCard: Joi.string()
    .max(DISCOUNT_CARD__LENGTH)
    .allow('')
    .pattern(new RegExp(`^[a-zA-Z0-9]{1,${DISCOUNT_CARD__LENGTH}}$`))
    .error(errorsResolver({
      max: `Номер дисконтной карты должно быть до ${DISCOUNT_CARD__LENGTH} символов`,
      pattern: `Номер дисконтной карты содержит некорректные символы`
    })),
  categories: Joi.object({ value: Joi.number(), label: Joi.any() })
    .allow(''),
  payerAccountNumber: Joi.string()
    .trim()
    .allow('')
    .max(PAYER_NUMBER_LENGTH)
    .pattern(new RegExp(`^[a-zA-Zа-яА-ЯёЁ0-9\\- ]{1,${PAYER_NUMBER_LENGTH}}$`))
    .error(errorsResolver({
      max: `Учётный номер плательщика должен быть до ${PAYER_NUMBER_LENGTH} символов`,
      pattern: `Учётный номер плательщика содержит некорректные символы`
    })),
  updateDate: Joi.date()
    .iso()
    .error(errorsResolver({
      dateFormat: `Некорректная дата`,
    }))
}

export const clientPersonalInfoScheme = Joi.object({
  client: Joi.object(clientPersonalInfoProperties)
})

export const otherInformationScheme = Joi.object({
  client: Joi.object(clientInformationProperties)
})

let clientOtherProperties = {
  rejectionReason: Joi.string()
    .max(PROHIBITION_LENGTH)
    .allow('')
    .pattern(new RegExp(`^[a-zA-Zа-яА-ЯёЁ0-9\\- ]{1,${PROHIBITION_LENGTH}}$`))
    .error(errorsResolver({
      max: `Основание для запрета обслуживания должно быть до ${PROHIBITION_LENGTH} символов`,
      pattern: `Основание для запрета обслуживания содержит некорректные символы`
    })),
  hearSource: Joi.string()
    .when('hearAbout', { is: 'Другое', otherwise: Joi.allow('') })
    .max(HEAR_SOURCE_LENGTH)
    .pattern(new RegExp(`^[a-zA-Zа-яА-ЯёЁ\\- ]{1,${HEAR_SOURCE_LENGTH}}$`))
    .error(errorsResolver({
      max: `Источник должен быть до ${HEAR_SOURCE_LENGTH} символов`,
      required: `Источник обязателен`,
      pattern: `Источник содержит некорректные символы`
    })),
  otherInformation: Joi.string()
    .allow('')
    .max(OTHER_INFORMATION_LENGTH)
    .pattern(patterns.GENERAL_TEXT(OTHER_INFORMATION_LENGTH))
    .error(errorsResolver({
      max: `Описание должно быть до ${OTHER_INFORMATION_LENGTH} символов`,
      pattern: `Описание содержит некорректные символы`
    })),
  hearAbout: Joi.string()
    .required()
    .error(errorsResolver({
      required: `Откуда узнали о нас обязательное поле`,
    }))
}

export const createClientOtherInfoScheme = (aimAndRelationshipFeature, otherAddHiddenContent) =>{
  if (!otherAddHiddenContent) {
    return Joi.object({
      client: Joi.object({
        otherInformation: clientOtherProperties.otherInformation
      })
    });
  }

  const properties = { ...clientOtherProperties };

  if (aimAndRelationshipFeature) {
    properties.aimAndRelationship = Joi.string()
      .required()
      .max(AIM_LENGTH)
      .pattern(new RegExp(`^[a-zA-Zа-яА-ЯёЁ\\- ]{1,${AIM_LENGTH}}$`))
      .error(errorsResolver({
        max: `Цели установления и характер отношений должно быть до ${AIM_LENGTH} символов`,
        required: `Цели установления и характер отношений обязательное поле`,
        pattern: `Цели установления и характер отношений содержит некорректные символы`
      }));
  } 

  return Joi.object({
    client: Joi.object(properties)
  });
}

export const clientCategoryScheme = Joi.object({
  client: Joi.object({
    categories: Joi.object({
      value: Joi.number(),
      label: Joi.string()
    }).allow('')
  })
});

const expandedObject = Joi.object({
  flag: Joi.bool(),
  payerAccountNumber: Joi.string()
    .trim()
    .allow('')
    .max(PAYER_NUMBER_LENGTH)
    .pattern(new RegExp(`^[a-zA-Zа-яА-ЯёЁ0-9\\- ]{1,${PAYER_NUMBER_LENGTH}}$`))
    .error(errorsResolver({
      max: `Учётный номер плательщика должен быть до ${PAYER_NUMBER_LENGTH} символов`,
      pattern: `Учётный номер плательщика содержит некорректные символы`
    }))
})

export const expandedClientScheme = Joi.object({ expanded: expandedObject })

export const createClientScheme = (aimAndRelationshipFeature, clientCategoryFeature) => {
  if (aimAndRelationshipFeature) {
    clientOtherProperties.aimAndRelationship = Joi.string()
      .required()
      .max(AIM_LENGTH)
      .pattern(new RegExp(`^[a-zA-Zа-яА-ЯёЁ\\- ]{1,${AIM_LENGTH}}$`))
      .error(errorsResolver({
        max: `Цели установления и характер отношений должно быть до ${AIM_LENGTH} символов`,
        required: `Цели установления и характер отношений обязательное поле`,
        pattern: `Цели установления и характер отношений содержит некорректные символы`
      }))
   
  }
  if (clientCategoryFeature) {
    clientOtherProperties.categories = Joi.object({ value: Joi.number(), label: Joi.any() })
      .allow('')
  }

  return Joi.object({
    client: Joi.object({
      ...clientPersonalInfoProperties,
      ...clientOtherProperties,
      ...clientInformationProperties,
    }),
    expanded: expandedObject
  })
}
